import styles from "./EmployeesAdministrationList.module.scss";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { Icon, IconType } from "../../../../common/icons/Icon";
import {
  constructEmployeeHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { useNavigate } from "react-router-dom";
import { classNames } from "primereact/utils";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { IEmployeesAdministrationEmployeeModel } from "./models/employeesAdministrationEmployeeModel";
import { UserRoleLabels } from "../../../../common/labels";
import { UserRole } from "../../../../common/enums";

export const EmployeesAdministration = () => {
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    href: routePaths.employeeAdd,
  });

  const { data: employees, isLoading } = useApiQuery<
    IEmployeesAdministrationEmployeeModel[]
  >({
    queryKey: [endpointsMetadata.employee.getAll],
    endpoint: endpointsMetadata.employee.getAll,
    globalIsLoading: false,
  });

  return (
    <ItemsList
      isLoading={isLoading}
      className={styles.root}
      title="Налични служители"
      noItemsText="Няма намерени служители."
      itemTemplate={(item, index, actionButtons, className) => {
        return (
          <EmployeeItem
            className={className}
            employee={item}
            key={item?.id ?? index}
            isLoading={isLoading}
            actionButtons={actionButtons}
          />
        );
      }}
      items={employees ?? []}
      swipeLeftActions={[
        {
          method: "delete",
          enablePrompt: true,
          promptMessage: "Сигурни ли сте, че искате да изтриете този служител?",
          iconType: IconType.DeleteIcon,
          endpoint: endpointsMetadata.employee.deleteEmployee,
          validator: (item: IEmployeesAdministrationEmployeeModel) =>
            !item.hasResigned,
          invalidateKeys: [[endpointsMetadata.employee.getAll]],
        },
        {
          method: "put",
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да възстановите този служител?",
          iconType: IconType.PutBackIcon,
          endpoint: endpointsMetadata.employee.putEmployeeBack,
          validator: (item: IEmployeesAdministrationEmployeeModel) =>
            item.hasResigned,
          invalidateKeys: [[endpointsMetadata.employee.getAll]],
        },
      ]}
      swipeRightActions={[
        {
          type: "edit",
          iconType: IconType.EditIcon,
          endpoint: routePaths.employeeEdit,
          validator: (item: IEmployeesAdministrationEmployeeModel) =>
            !item.hasResigned,
        },
      ]}
      deletedFilter={{
        storageKey: "employees",
        isDeleted: (item: IEmployeesAdministrationEmployeeModel) =>
          !!item.hasResigned,
        sectionTitle: "Изтрити служители",
        toggleLabel: "Покажи изтритите служители",
      }}
      showItemsCount
      enableSearch
      fullHeight
    />
  );
};

interface IEmployeesAdministrationEmployeeProps {
  className?: string;
  employee?: IEmployeesAdministrationEmployeeModel;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

const EmployeeItem = ({
  className,
  employee,
  isLoading,
  actionButtons,
}: IEmployeesAdministrationEmployeeProps) => {
  const navigate = useNavigate();

  const onClick = () => {
    if (employee) {
      const href = constructEmployeeHref(employee.id);
      navigate(href);
    }
  };

  const combinedPhoneAndEmail = () => {
    if (!!employee?.phoneNumber && !!employee.email) {
      return `${employee.phoneNumber} | ${employee.email}`;
    } else if (!!employee?.phoneNumber) {
      return employee.phoneNumber;
    } else if (!!employee?.email) {
      return employee.email;
    }

    return;
  };

  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_resigned]: employee?.hasResigned,
      })}
      onClick={onClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_name}>
              {employee?.firstName} {employee?.lastName}
              <span className={styles.item_left_name_id}>
                (ID: {employee?.id}
                {employee?.originalStudio && `, ${employee.originalStudio}`})
              </span>
            </div>
          }
          width={"60%"}
        />
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_secondary_item}>
              {combinedPhoneAndEmail()}
            </div>
          }
          width={"70%"}
        />
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_secondary_item}>
              {UserRoleLabels[employee?.role ?? UserRole.Employee_Instructor]}
            </div>
          }
          width={"51%"}
        />
      </div>
      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
